import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { categories, channels } from "@/mocks/streams";
import StreamCard from "@/components/feature/StreamCard";
import FilterBar from "@/components/feature/FilterBar";
import type { FilterState } from "@/components/feature/FilterBar";
import { filterAndSortChannels } from "@/lib/filterChannels";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 8;

const defaultFilters: FilterState = { status: "all", level: "all", language: "all", sort: "popularity" };

export default function Category() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const category = categories.find((item) => item.id === categoryId);
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  usePageMeta(
    category ? `${category.name} - 有因直播` : "直播分类 - 有因直播",
    category
      ? `${category.description} 有因直播为你汇集${category.name}类热门直播频道，支持筛选与排序。`
      : "有因直播直播分类页，汇集游戏、娱乐、生活等多元直播频道。",
  );

  useEffect(() => {
    setFilters(defaultFilters);
    setVisibleCount(PAGE_SIZE);
  }, [categoryId]);

  const categoryChannels = useMemo(
    () => channels.filter((channel) => channel.categoryId === categoryId),
    [categoryId],
  );

  const filtered = useMemo(() => filterAndSortChannels([...categoryChannels], filters), [categoryChannels, filters]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filters]);

  const updateFilters = (next: Partial<FilterState>) => setFilters((prev) => ({ ...prev, ...next }));
  const resetFilters = () => setFilters(defaultFilters);

  if (!category) {
    return (
      <div className="w-full px-4 py-20 md:px-6">
        <div className="mx-auto max-w-lg rounded-lg border border-background-200/70 bg-background-100 p-10 text-center">
          <span className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-emotion-sad-line text-2xl"></i>
          </span>
          <h1 className="mt-4 font-heading text-xl font-semibold text-foreground-950">未找到该直播分类</h1>
          <p className="mt-2 text-sm text-foreground-400">你访问的分类可能已调整或不存在，欢迎返回首页浏览其他分类。</p>
          <Link
            to="/"
            className="mt-6 inline-flex rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const visible = filtered.slice(0, visibleCount);
  const recommendations = channels.filter((channel) => channel.isLive && channel.categoryId !== category.id).slice(0, 3);

  return (
    <div className="w-full px-4 py-6 md:px-6 md:py-8">
      <nav className="flex flex-wrap items-center gap-1 text-xs text-foreground-400" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-500">
          首页
        </Link>
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-arrow-right-s-line"></i>
        </span>
        <span>直播分类</span>
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-arrow-right-s-line"></i>
        </span>
        <span className="text-foreground-700">{category.name}</span>
      </nav>

      <section className="mt-5 flex flex-col gap-4 rounded-xl border border-background-200/70 bg-background-100 p-5 md:flex-row md:items-center md:justify-between md:p-7">
        <div className="flex items-start gap-4">
          <span className="w-14 h-14 flex items-center justify-center rounded-lg bg-primary-500/12 text-primary-500">
            <i className={`${category.icon} text-3xl`}></i>
          </span>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950">{category.name}直播</h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-500">{category.description}</p>
          </div>
        </div>
        <div className="flex gap-4 md:flex-col md:gap-1">
          <div className="rounded-lg bg-background-50 px-4 py-3 text-center">
            <p className="font-heading text-xl font-bold text-foreground-950">{categoryChannels.length}</p>
            <p className="text-xs text-foreground-400">频道总数</p>
          </div>
          <div className="rounded-lg bg-background-50 px-4 py-3 text-center">
            <p className="font-heading text-xl font-bold text-primary-500">
              {categoryChannels.filter((channel) => channel.isLive).length}
            </p>
            <p className="text-xs text-foreground-400">正在直播</p>
          </div>
        </div>
      </section>

      <div className="mt-6">
        <FilterBar filters={filters} onChange={updateFilters} onReset={resetFilters} />
      </div>

      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-foreground-400">
          共找到 <span className="text-primary-500">{filtered.length}</span> 个直播频道
        </p>
      </div>

      {filtered.length === 0 ? (
        <div className="mt-6 rounded-lg border border-background-200/70 bg-background-100 p-10 text-center">
          <span className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-search-eye-line text-2xl"></i>
          </span>
          <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">当前筛选下没有匹配的直播</h2>
          <p className="mt-2 text-sm text-foreground-400">试着放宽筛选条件，或者看看下面为你推荐的直播内容。</p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-5 inline-flex rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            重置筛选条件
          </button>

          <div className="mt-8 text-left">
            <h3 className="mb-4 font-heading text-sm font-semibold text-foreground-900">为你推荐</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {recommendations.map((channel) => (
                <StreamCard key={channel.id} channel={channel} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {visible.map((channel) => (
              <StreamCard key={channel.id} channel={channel} />
            ))}
          </div>

          {visibleCount < filtered.length && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                className="flex items-center gap-2 rounded-full border border-background-300 px-6 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-500 whitespace-nowrap"
              >
                加载更多
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-down-s-line"></i>
                </span>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}